import React, { Suspense } from "react";
import { Helmet } from "react-helmet";
import { ErrorBoundary } from "react-error-boundary";
import { useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { Footer } from "@/components/Footer";
import { LoadingState } from "@/components/layout/LoadingState";
import { TechnologyHero } from "@/components/technology/TechnologyHero";
import { TabbedTechnologyContent } from "@/components/technology/TabbedTechnologyContent";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

const ErrorFallback = ({ error, resetErrorBoundary }) => (
  <div className="text-center p-6 bg-destructive/10 rounded-lg" role="alert">
    <h2 className="text-xl font-semibold text-destructive mb-2">Something went wrong</h2>
    <p className="text-gray-600 mb-4">{error.message}</p>
    <Button
      onClick={resetErrorBoundary}
      variant="default"
      className="bg-primary text-white hover:bg-primary/90"
    >
      Try again
    </Button>
  </div>
);

const Technology = () => {
  const prefersReducedMotion = useReducedMotion();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    "@id": "https://scienzahealth.com/technology#webpage",
    "url": "https://scienzahealth.com/technology",
    "name": "AI-Powered Cognitive Health Technology | Scienza Health",
    "description": "Discover our advanced AI technology for cognitive health monitoring. Real-time analysis, early detection, and continuous assessment for better healthcare outcomes.",
    "isPartOf": {
      "@id": "https://scienzahealth.com/#website"
    },
    "about": {
      "@type": "SoftwareApplication",
      "name": "Scienza Health Platform",
      "applicationCategory": "HealthcareApplication",
      "operatingSystem": "Web-based",
      "offers": {
        "@type": "Offer",
        "category": "Cognitive Health Monitoring"
      }
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [{
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://scienzahealth.com"
      }, {
        "@type": "ListItem",
        "position": 2,
        "name": "Technology",
        "item": "https://scienzahealth.com/technology"
      }]
    },
    "mainEntity": {
      "@type": "HealthcareService",
      "name": "Scienza Health Cognitive Monitoring",
      "description": "AI-powered cognitive health monitoring and early detection platform",
      "healthcareSpecialty": ["Cognitive Health", "Digital Health", "AI in Healthcare"],
      "availableService": [
        "Real-time Cognitive Monitoring",
        "Early Detection",
        "Digital Twin Technology",
        "Clinical Decision Support",
        "Enterprise Integration"
      ]
    }
  };

  return (
    <>
      <Helmet>
        <title>The Science Behind Every Conversation | Scienza Health</title>
        <meta
          name="description"
          content="Transforming routine healthcare calls into powerful health insights through voice AI and clinically validated speech biomarkers."
        />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 bg-primary text-white px-4 py-2 rounded z-50"
      >
        Skip to main content
      </a>
      
      <main id="main-content" className="min-h-screen" style={{ background: 'var(--gradient-warm)' }}>
        <div className="pt-24">
          <div className="max-w-7xl mx-auto">
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <Suspense fallback={<LoadingState />}>
        <TechnologyHero />
        <TabbedTechnologyContent />
                
                <div className="mt-12 text-center pb-16">
                  <Button 
                    size="lg"
                    className="group bg-primary text-white hover:bg-primary/90 transition-colors"
                    asChild
                  >
                    <Link to="/subscription">
                      Get Started
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </Suspense>
            </ErrorBoundary>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Technology;